import { useEffect, useState } from 'react'

import _ from 'lodash'
import { ItList } from 'types/graphql'

import { routes } from '@redwoodjs/router'

import { FREE_IT_LIST_ACCESS_LINK_TEMPLATE_NAME } from 'src/lib/constants'
import { useAnalytics } from 'src/lib/hooks/analytics/useAnalytics'
import type { RegistrationFormType } from 'src/lib/hooks/auth/useUserRegistration'
import useInitUser from 'src/lib/hooks/mutations/useInitUser'
import { isInIFrame, pathJoin, slugify } from 'src/lib/utils'

const SHOW_SUCCESS_OVERLAY_TIMEOUT = 3000

export default function useUnlockWithEmail({
  title,
  creator,
  itListId,
}: {
  itListId: number
  title: string
  creator: ItList['creator']
}) {
  const [errorMessage, setErrorMessage] = useState('')
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false)
  const [init, { loading }] = useInitUser()

  const { track } = useAnalytics()

  const onSubmit = async (data: RegistrationFormType) => {
    // (see Bot prevention page in Confluence)
    if (isInIFrame()) {
      setErrorMessage('An error occured')
      return
    }
    try {
      await init({
        variables: {
          input: {
            ...data,
            emailTemplateName: FREE_IT_LIST_ACCESS_LINK_TEMPLATE_NAME,
            forceActivationCodeAuth: true,
            emailTemplateVariables: JSON.stringify({
              title,
              coverUrl: pathJoin(
                process.env.HOME_URL,
                window.RWJS_API_URL,
                `getItListCover?id=${itListId}&w=750`
              ),
              creatorUsername: creator.username,
            }),
            creator: false,
            activateUserRoute: routes.itList({
              id: itListId,
              title: slugify(title),
            }),
          },
        },
      })
      track({
        eventName: 'Email Paywall Field Submitted',
        props: {
          isOwner: false,
          owningCreatorEmail: creator.email,
          owningCreatorId: `${creator.id}`,
          owningCreatorUsername: creator.username,
          productId: `${itListId}`,
          fieldValue: data.email,
        },
      })
      setShowSuccessOverlay(true)
    } catch (err) {
      setErrorMessage(`Oops, that didn't send. Please try again.`)
    }
  }

  const onFocus: React.FocusEventHandler = () => {
    track({
      eventName: 'Email Paywall Field Focused',
      props: {
        isOwner: false,
        owningCreatorEmail: creator.email,
        owningCreatorId: `${creator.id}`,
        owningCreatorUsername: creator.username,
        productId: `${itListId}`,
      },
    })
  }

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (showSuccessOverlay) {
      timer = setTimeout(() => {
        setShowSuccessOverlay(false)
      }, SHOW_SUCCESS_OVERLAY_TIMEOUT)
    }
    return () => clearTimeout(timer)
  }, [showSuccessOverlay, setShowSuccessOverlay])

  return { onSubmit, errorMessage, loading, showSuccessOverlay, onFocus }
}
